import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    CL_ORDER_PAGE_SIZE_OPTIONS,
    CL_ORDER_SEARCH_COLUMNS,
    CL_ORDER_SORT_FIELDS,
    CL_ORDER_STATUS_OPTIONS,
    CL_ORDER_TYPE_OPTIONS,
    DEFAULT_CL_ORDER_FILTERS,
    DEFAULT_CL_ORDER_SEARCH_COLUMN,
} from './subparts/clOrderRequestQuery';

function ClOrderRequestSearchForm({
    onSearch,
    onShowAll,
    isLoading,
    searchTerm: externalSearchTerm,
    searchColumn: externalSearchColumn,
    type,
    status,
    sortBy,
    sortOrder,
    pageSize,
    onTypeChange,
    onStatusChange,
    onSortByChange,
    onSortOrderChange,
    onPageSizeChange,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const { register, handleSubmit, watch, setValue } = useForm({
        defaultValues: {
            searchTerm: externalSearchTerm || '',
            searchColumn: externalSearchColumn || DEFAULT_CL_ORDER_SEARCH_COLUMN,
        },
    });

    const searchTerm = watch('searchTerm', '');
    const searchColumn = watch('searchColumn', DEFAULT_CL_ORDER_SEARCH_COLUMN);
    const selectedColumn = CL_ORDER_SEARCH_COLUMNS.find((option) => option.value === searchColumn);
    const searchPlaceholder = selectedColumn
        ? `Search by ${selectedColumn.label.toLowerCase()}...`
        : 'Search CL order requests...';

    const onSubmit = (data) => onSearch(data.searchColumn, data.searchTerm);

    const handleClear = () => {
        setValue('searchTerm', '');
        onSearch(searchColumn, '');
    };

    const handleColumnChange = (column) => {
        setValue('searchColumn', column);
        if (searchTerm.trim()) {
            onSearch(column, searchTerm);
        }
    };

    const hasAdvancedFilters =
        (type && type !== DEFAULT_CL_ORDER_FILTERS.type) ||
        (status && status !== DEFAULT_CL_ORDER_FILTERS.status);

    return (
        <div className="optical-dispensary-filters">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="optical-dispensary-filters__row">
                    <div className="optical-dispensary-filters__search input-group input-group-sm">
                        <span className="input-group-text optical-dispensary-filters__search-by-label">
                            Search By
                        </span>
                        <select
                            className="form-select form-select-sm optical-dispensary-filters__search-by"
                            aria-label="Search By"
                            {...register('searchColumn')}
                            onChange={(event) => handleColumnChange(event.target.value)}
                            disabled={isLoading}
                        >
                            {CL_ORDER_SEARCH_COLUMNS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <span className="input-group-text optical-dispensary-filters__search-icon bg-white border-start-0 border-end-0">
                            <i className="bi bi-search text-muted" />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm border-start-0"
                            placeholder={searchPlaceholder}
                            {...register('searchTerm')}
                            disabled={isLoading}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary optical-dispensary-filters__clear-btn"
                                onClick={handleClear}
                                disabled={isLoading}
                                aria-label="Clear search"
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-light-primary optical-dispensary-filters__search-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                                    Searching
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-search me-1" />
                                    Search
                                </>
                            )}
                        </button>
                    </div>

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary optical-dispensary-filters__show-all-btn"
                        onClick={onShowAll}
                        disabled={isLoading}
                    >
                        <i className="bi bi-collection me-1" />
                        Show All
                    </button>

                    <button
                        type="button"
                        className={`btn btn-sm optical-dispensary-filters__toggle${
                            filtersOpen ? ' btn-light-primary is-open' : ' btn-outline-secondary'
                        }`}
                        onClick={() => setFiltersOpen((open) => !open)}
                        aria-expanded={filtersOpen}
                        aria-controls="optical-dispensary-cl-order-advanced-filters"
                    >
                        <i className="bi bi-sliders me-1" aria-hidden="true" />
                        Filters
                        {hasAdvancedFilters && <span className="optical-dispensary-filters__badge" />}
                        <i
                            className={`bi bi-chevron-${filtersOpen ? 'up' : 'down'} ms-1 optical-dispensary-filters__toggle-chevron`}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </form>

            {filtersOpen && (
                <div
                    id="optical-dispensary-cl-order-advanced-filters"
                    className="optical-dispensary-filters__advanced"
                >
                    <label className="optical-dispensary-filters__control mb-0">
                        <span className="optical-dispensary-filters__control-label">Status</span>
                        <select
                            className="form-select form-select-sm"
                            value={status}
                            onChange={(event) => onStatusChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Filter by status"
                        >
                            {CL_ORDER_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="optical-dispensary-filters__control mb-0">
                        <span className="optical-dispensary-filters__control-label">Fulfillment</span>
                        <select
                            className="form-select form-select-sm"
                            value={type}
                            onChange={(event) => onTypeChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Filter by fulfillment type"
                        >
                            {CL_ORDER_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="optical-dispensary-filters__control mb-0">
                        <span className="optical-dispensary-filters__control-label">Sort</span>
                        <select
                            className="form-select form-select-sm"
                            value={sortBy}
                            onChange={(event) => onSortByChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Sort by"
                        >
                            {CL_ORDER_SORT_FIELDS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="optical-dispensary-filters__control mb-0">
                        <span className="optical-dispensary-filters__control-label">Order</span>
                        <select
                            className="form-select form-select-sm optical-dispensary-filters__order"
                            value={sortOrder}
                            onChange={(event) => onSortOrderChange?.(event.target.value)}
                            disabled={isLoading}
                            aria-label="Sort order"
                        >
                            <option value="DESC">Desc</option>
                            <option value="ASC">Asc</option>
                        </select>
                    </label>

                    <label className="optical-dispensary-filters__control mb-0">
                        <span className="optical-dispensary-filters__control-label">Show</span>
                        <select
                            className="form-select form-select-sm optical-dispensary-filters__page-size"
                            value={pageSize}
                            onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
                            disabled={isLoading}
                            aria-label="Entries per page"
                        >
                            {CL_ORDER_PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span className="optical-dispensary-filters__control-suffix">entries</span>
                    </label>
                </div>
            )}
        </div>
    );
}

export default ClOrderRequestSearchForm;
